import React from 'react';
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  darken,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { WidgetCard } from 'components/WidgetCard/WidgetCard';
import { SvgIcon } from 'components/SvgIcon/SvgIcon';
import { ReactComponent as BloodPressureSvg } from 'assets/icons/Bloodpressure.svg';
import { ReactComponent as BloodPressureRequestedSvg } from 'assets/icons/BloodPressureRequested.svg';
import { BiometricDataPoint, BiometricsResponse } from 'services/biometricsService';
import { BloodPressureChart } from './BloodPressureChart';

const CHART_HEIGHT = 240;
const noop = () => undefined;

// Room around the card so its box shadow isn't clipped by the parent (same as WeightWidgetCard).
const CardSizer = styled(Box)(({ theme }) => ({
  width: '100%',
  boxSizing: 'border-box',
  padding: theme.spacing(0.5),
}));

// WidgetCard's header icon padding (8px mobile / 16px tablet+), icon (20px / 24px) and 8px gap,
// so content lines up with the title.
const TITLE_INDENT_MOBILE = 36;
const TITLE_INDENT_TABLET = 48;

const ReadingSummary = styled(Box)(({ theme }) => ({
  paddingLeft: TITLE_INDENT_MOBILE,
  [theme.breakpoints.up('sm')]: {
    paddingLeft: TITLE_INDENT_TABLET,
  },
}));

const ReadingRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'baseline',
  gap: theme.spacing(0.5),
}));

const ReadingValue = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.grey[900],
}));

const ReadingUnits = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[900],
}));

// grey[600] is only 3.6:1 on white (fails AA); darkened 15% it's ~4.9:1.
// Matches the card title size: callout on mobile, title3 on tablet+.
const ReadingLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.callout,
  color: darken(theme.palette.grey[600], 0.15),
  [theme.breakpoints.up('sm')]: {
    ...theme.typography.title3,
  },
}));

const ChartWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(1),
}));

const CenteredState = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(6),
}));

const RequestedState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: theme.spacing(3),
  padding: theme.spacing(2, 2, 2),
}));

// Matches the card title size: callout on mobile, title3 on tablet+.
const RequestedText = styled(Typography)(({ theme }) => ({
  ...theme.typography.callout,
  color: theme.palette.grey[800],
  maxWidth: 280,
  [theme.breakpoints.up('sm')]: {
    ...theme.typography.title3,
  },
}));

// "2026-09-20" -> "9/20"; reads the ISO parts directly so the label never shifts a day by timezone.
const formatDateLabel = (date: string) => {
  const [, month, day] = date.slice(0, 10).split('-');
  return `${Number(month)}/${day}`;
};

// "2024-05-22T12:00:00Z" -> "5/22/24" in the member's local time.
const formatRequestedOn = (timestamp: string) => {
  const date = new Date(timestamp);
  return `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear()).slice(-2)}`;
};

const READINGS_SHOWN = 7;

interface BloodPressureReading {
  date: string;
  timestamp: string;
  systolic: number;
  diastolic: number;
}

const toReadings = (points: BiometricDataPoint[]): BloodPressureReading[] =>
  points
    .flatMap(({ date, timestamp, values }) => {
      const [systolic, diastolic] = values ?? [];
      return systolic != null && diastolic != null
        ? [{ date, timestamp, systolic, diastolic }]
        : [];
    })
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp));

export interface BloodPressureWidgetProps {
  data?: BiometricsResponse;
  isLoading?: boolean;
  isError?: boolean;
  /** A pending blood pressure tracker request; shown in place of readings. */
  requestedTracker?: { programName: string; requestedOn: string };
  /** Opens the detail page; the card is only clickable when this is set and no tracker is pending. */
  onClick?: () => void;
}

export const BloodPressureWidget = ({
  data,
  isLoading = false,
  isError = false,
  requestedTracker,
  onClick,
}: BloodPressureWidgetProps) => {
  const { translation } = useTranslationContext();
  const theme = useTheme();
  const isTabletUp = useMediaQuery(theme.breakpoints.up('sm'));

  const readings = toReadings(data?.data ?? []).slice(-READINGS_SHOWN);
  const lastReading = readings[readings.length - 1];
  const units = data?.units ?? 'mmHg';

  const renderBody = () => {
    if (requestedTracker) {
      return (
        <RequestedState>
          <BloodPressureRequestedSvg aria-hidden="true" width={112} height={104} />
          <RequestedText>
            {translation('tracker_requested', {
              arg1: requestedTracker.programName,
              arg2: formatRequestedOn(requestedTracker.requestedOn),
            })}
          </RequestedText>
        </RequestedState>
      );
    }

    if (isLoading) {
      return (
        <CenteredState>
          <CircularProgress aria-label={translation('blood_pressure')} />
        </CenteredState>
      );
    }

    if (isError) {
      return (
        <CenteredState>
          <Typography variant="footnote">{translation('data_unable_to_load')}</Typography>
        </CenteredState>
      );
    }

    return (
      <>
        <ReadingSummary>
          <ReadingRow>
            <ReadingValue variant="title1">
              {lastReading ? `${lastReading.systolic}/${lastReading.diastolic}` : '--'}
            </ReadingValue>
            <ReadingUnits variant="title3">{units}</ReadingUnits>
          </ReadingRow>
          <ReadingLabel>{translation(lastReading ? 'last_reading' : 'no_readings')}</ReadingLabel>
        </ReadingSummary>
        <ChartWrapper>
          <BloodPressureChart
            height={CHART_HEIGHT}
            // Left margin puts the y-axis line under the title; tick labels sit in that margin.
            margin={{
              top: 16,
              right: 0,
              bottom: isTabletUp ? 32 : 24,
              left: isTabletUp ? TITLE_INDENT_TABLET : TITLE_INDENT_MOBILE,
            }}
            dates={readings.map((reading) => formatDateLabel(reading.date))}
            systolic={readings.map((reading) => reading.systolic)}
            diastolic={readings.map((reading) => reading.diastolic)}
          />
        </ChartWrapper>
      </>
    );
  };

  return (
    <CardSizer>
      <WidgetCard
        icon={<SvgIcon component={BloodPressureSvg} inheritViewBox />}
        title={translation('blood_pressure')}
        headerSx={requestedTracker ? { pt: 3, pl: 6 } : undefined}
        showChevron={!!onClick && !requestedTracker}
        onClick={onClick ?? noop}
      >
        {renderBody()}
      </WidgetCard>
    </CardSizer>
  );
};
